import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Globe, Send, MessageSquare } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const ChatRoomRequest = () => {
  const navigate = useNavigate();
  
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [languageName, setLanguageName] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [existingRooms, setExistingRooms] = useState([]);

  useEffect(() => {
    fetchLanguages();
    fetchExistingRooms();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await api.get('/languages');
      // API returns array directly, not wrapped in {languages: [...]}
      const languageList = Array.isArray(response.data) ? response.data : (response.data.languages || []);
      setLanguages(languageList);
    } catch (err) {
      console.error('Failed to fetch languages:', err);
    }
  };

  const fetchExistingRooms = async () => {
    try {
      const response = await api.get('/chat/rooms');
      const roomLanguages = response.data.rooms.map(r => r.language_code);
      setExistingRooms(roomLanguages);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLanguage) {
      toast.error('Please select a language');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await api.post('/chat/room/request', {
        language_code: selectedLanguage,
        language_name: languageName,
        reason: reason.trim() || null
      });
      
      if (response.data.status === 'created') {
        toast.success('Room created! Redirecting...');
        navigate(`/community/chat/${response.data.room_id}?mode=participate`);
      } else if (response.data.status === 'voted') {
        toast.success(`Vote recorded! ${response.data.current_votes}/5 votes`);
        navigate('/community');
      } else {
        toast.success('Request submitted! 5 votes needed to create the room.');
        navigate('/community');
      }
    } catch (err) {
      console.error('Failed to request room:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter out languages that already have rooms
  const availableLanguages = languages.filter(
    lang => !existingRooms.includes(lang.code) && lang.code !== 'general'
  );

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-lg mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/community')}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Community
        </button>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Request Chat Room</h1>
          <p className="text-slate-400">
            Request a new language-specific chat room. 5 votes needed to create a room.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Language Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <Globe className="w-4 h-4 inline mr-2" />
              Language *
            </label>
            <select
              value={selectedLanguage}
              onChange={(e) => {
                const selected = languages.find(l => l.code === e.target.value);
                setSelectedLanguage(e.target.value);
                setLanguageName(selected?.name || e.target.value);
              }}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-amber-500"
              required
            >
              <option value="">Select a language...</option>
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
            {availableLanguages.length === 0 && (
              <p className="text-sm text-slate-500 mt-2">
                All available languages already have chat rooms!
              </p>
            )}
          </div>

          {/* Reason (Optional) */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why do you want this chat room?"
              rows={3}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
              maxLength={500}
            />
          </div>

          {/* Info */}
          <div className="p-4 rounded-lg bg-slate-800 border border-slate-700">
            <h3 className="text-white font-medium mb-2">How it works:</h3>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Your request counts as the first vote</li>
              <li>• Other users can vote for your request</li>
              <li>• When 5 votes are reached, the room is automatically created</li>
              <li>• Once created, rooms are permanent even if empty</li>
            </ul>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !selectedLanguage}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium transition-colors"
            data-testid="submit-request-btn"
          >
            <Send className="w-5 h-5" />
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatRoomRequest;
